import React from "react";

// Decorative mandala — layered concentric geometric flower
export const Mandala = ({ className = "", size = 240, color = "#C9A227", strokeWidth = 0.8 }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 240 240"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        aria-hidden="true"
    >
        <circle cx="120" cy="120" r="6" />
        <circle cx="120" cy="120" r="22" />
        <circle cx="120" cy="120" r="42" />
        <circle cx="120" cy="120" r="62" strokeDasharray="2 4" />
        <circle cx="120" cy="120" r="86" />
        <circle cx="120" cy="120" r="112" strokeDasharray="1 3" />
        {Array.from({ length: 16 }).map((_, i) => {
            const a = (Math.PI * 2 * i) / 16;
            const x1 = 120 + Math.cos(a) * 22;
            const y1 = 120 + Math.sin(a) * 22;
            const x2 = 120 + Math.cos(a) * 112;
            const y2 = 120 + Math.sin(a) * 112;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        {Array.from({ length: 8 }).map((_, i) => {
            const a = (Math.PI * 2 * i) / 8;
            const cx = 120 + Math.cos(a) * 62;
            const cy = 120 + Math.sin(a) * 62;
            return <circle key={i} cx={cx} cy={cy} r="10" />;
        })}
        {Array.from({ length: 12 }).map((_, i) => {
            const a = (Math.PI * 2 * i) / 12;
            const cx = 120 + Math.cos(a) * 86;
            const cy = 120 + Math.sin(a) * 86;
            return <circle key={i} cx={cx} cy={cy} r="4" />;
        })}
    </svg>
);

// Paisley (mango / kairi) motif
export const Paisley = ({ className = "", size = 24, color = "#C9A227" }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="1.2" aria-hidden="true">
        <path d="M16 3c8 2 12 8 12 14 0 6-5 12-12 12-4 0-8-2-8-6 0-3 2-5 5-5 2 0 4 1 4 3" />
        <path d="M16 10c3 1 5 3 5 6 0 2-2 4-4 4" />
        <circle cx="16" cy="16" r="1.2" fill={color} />
    </svg>
);

// Gold foil divider with paisleys on either side
export const PaisleyDivider = ({ className = "" }) => (
    <div className={`flex items-center justify-center gap-4 ${className}`} data-testid="paisley-divider">
        <span className="gold-divider flex-1 max-w-[120px]" />
        <Paisley size={22} />
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)]" />
        <Paisley size={22} className="scale-x-[-1]" />
        <span className="gold-divider flex-1 max-w-[120px]" />
    </div>
);

// Small ornamental lotus icon for section headings
export const Lotus = ({ className = "", size = 28, color = "#7B1E2B" }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="1.2" aria-hidden="true">
        <path d="M16 26c-6 0-11-4-11-4s2-6 7-6" />
        <path d="M16 26c6 0 11-4 11-4s-2-6-7-6" />
        <path d="M16 26c-3 0-6-3-6-3s1-6 6-8" />
        <path d="M16 26c3 0 6-3 6-3s-1-6-6-8" />
        <path d="M16 26V10" />
        <circle cx="16" cy="8" r="2" fill={color} />
    </svg>
);

// Corner ornament — quarter mandala for section corners
export const CornerOrnament = ({ className = "", size = 100, color = "#C9A227", flip = "" }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        stroke={color}
        strokeWidth="0.7"
        style={{ transform: flip }}
        aria-hidden="true"
    >
        <path d="M0 0 C 40 5 60 25 65 65 L 100 65 L 100 0 Z" fill="none" />
        <path d="M0 0 C 30 8 50 28 55 55" />
        <path d="M0 0 C 20 12 35 30 42 45" />
        <circle cx="65" cy="35" r="4" />
        <circle cx="35" cy="65" r="4" />
        <circle cx="50" cy="50" r="2" fill={color} />
    </svg>
);

// Diya (lamp) icon
export const Diya = ({ className = "", size = 22, color = "#B4531A" }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="1.4" aria-hidden="true">
        <path d="M4 20 Q16 30 28 20 Z" fill={color} fillOpacity="0.15" />
        <path d="M16 20 V14" />
        <path d="M16 8 C 14 10 14 12 16 14 C 18 12 18 10 16 8 Z" fill={color} fillOpacity="0.35" />
    </svg>
);

// Decorative Bangle SVG icon — different rendering styles for material types
export const BangleIcon = ({ variant = "Kanganya", size = 120, className = "" }) => {
    const styles = {
        Kanganya: { ring: "#7B1E2B", inner: "#E8CE68", accent: "#1F5D45", dots: 12 },
        kundan: { ring: "#C9A227", inner: "#FDF2C5", accent: "#7B1E2B", dots: 12 },
        glass: { ring: "#B4531A", inner: "#F5EBD6", accent: "#1F5D45", dots: 20 },
        lac: { ring: "#7B1E2B", inner: "#E8CE68", accent: "#FBF5E9", dots: 6 },
        metal: { ring: "#B4531A", inner: "#D4A574", accent: "#7B1E2B", dots: 8 },
        silver: { ring: "#8B8B8B", inner: "#D5D5D5", accent: "#5C1420", dots: 10 },
        meenakari: { ring: "#1F5D45", inner: "#C9A227", accent: "#7B1E2B", dots: 14 },
        polki: { ring: "#C9A227", inner: "#F5EBD6", accent: "#B4531A", dots: 16 },
        bridal: { ring: "#7B1E2B", inner: "#FBF5E9", accent: "#C9A227", dots: 18 },
    };
    const s = styles[variant] || styles.Kanganya;
    const cx = 60, cy = 60, outerR = 52, innerR = 36;
    return (
        <svg className={className} width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
            {/* Outer ring */}
            <circle cx={cx} cy={cy} r={outerR} fill="none" stroke={s.ring} strokeWidth="10" />
            {/* Inner glow ring */}
            <circle cx={cx} cy={cy} r={outerR - 4} fill="none" stroke={s.inner} strokeWidth="2" opacity="0.7" />
            {/* Decorative dots on the ring */}
            {Array.from({ length: s.dots }).map((_, i) => {
                const a = (Math.PI * 2 * i) / s.dots;
                const dx = cx + Math.cos(a) * outerR;
                const dy = cy + Math.sin(a) * outerR;
                return <circle key={i} cx={dx} cy={dy} r="2.5" fill={s.accent} />;
            })}
            {/* Inner hole */}
            <circle cx={cx} cy={cy} r={innerR} fill="none" stroke={s.ring} strokeWidth="1" opacity="0.6" />
            {/* Central paisley accent */}
            <circle cx={cx} cy={cy} r="4" fill={s.accent} opacity="0.4" />
        </svg>
    );
};
